import { useState, useEffect } from 'react';
import invoiceController from '../../controllers/invoiceController';
import Invoice from '../../models/Invoice'; // backend se aane wala plain object ko wrap karta hai taaki getStatusColor()/isOverdue()/getBalanceAmount() jaise helper methods mil jaayen
import { Plus, Search, Download, Eye, Edit2, Trash2, X, AlertCircle, CheckCircle, Wallet } from 'lucide-react';

const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [formData, setFormData] = useState({
    customerName: '', email: '', phone: '', company: '', invoiceDate: '', dueDate: '',
    status: 'Draft', totalAmount: 0, paidAmount: 0, notes: ''
  });
  const [formErrors, setFormErrors] = useState([]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => { fetchInvoices(); }, []);

  useEffect(() => {
    let result = [...invoices];
    if (searchQuery) {
      result = result.filter((i) =>
        (i.customerName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (i.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (i.invoiceId || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    if (filterStatus !== 'All') result = result.filter((i) => i.status === filterStatus);
    setFiltered(result);
    setCurrentPage(1);
  }, [invoices, searchQuery, filterStatus]);

  // 🆕 method name fix: getAll() ki jagah ab getAllInvoices() call hoga (real controller ke hisaab se)
  const fetchInvoices = async () => {
    setLoading(true);
    const result = await invoiceController.getAllInvoices();
    if (result.success) {
      // backend ka raw data Invoice class mein wrap karo taaki table/badges mein helper methods chal sakein
      setInvoices((result.data || []).map((d) => new Invoice(d)));
    } else {
      alert('Error: ' + result.message);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setFormErrors([]);
  };

  const handleAdd = () => {
    setFormData({ customerName: '', email: '', phone: '', company: '', invoiceDate: '', dueDate: '', status: 'Draft', totalAmount: 0, paidAmount: 0, notes: '' });
    setFormErrors([]);
    setShowAddModal(true);
  };

  // 🆕 create() -> createInvoice()
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    const result = await invoiceController.createInvoice(formData);
    if (result.success) { setShowAddModal(false); fetchInvoices(); alert('✅ Invoice created!'); }
    else setFormErrors(result.errors || [result.message]);
    setFormSubmitting(false);
  };

  const handleEdit = (i) => {
    setSelected(i);
    setFormData({ ...i, dueDate: i.dueDate?.split('T')[0] || '' });
    setFormErrors([]);
    setShowEditModal(true);
  };

  // 🆕 update() -> updateInvoice()
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    const result = await invoiceController.updateInvoice(selected.id, formData);
    if (result.success) { setShowEditModal(false); fetchInvoices(); alert('✅ Invoice updated!'); }
    else setFormErrors(result.errors || [result.message]);
    setFormSubmitting(false);
  };

  const handleView = (i) => { setSelected(i); setShowViewModal(true); };

  // 🆕 delete() -> deleteInvoice()
  const handleDelete = async (i) => {
    try {
      if (!window.confirm(`Delete invoice for ${i.customerName}?`)) return;
      const result = await invoiceController.deleteInvoice(i.id || i._id);
      if (result?.success) { await fetchInvoices(); alert('✅ Invoice deleted!'); }
      else alert(result?.message || '❌ Failed to delete invoice');
    } catch (error) {
      console.error('Delete invoice error:', error);
      alert('❌ Something went wrong while deleting');
    }
  };

  const handleRecordPayment = (i) => { setSelected(i); setPaymentAmount(i.getBalanceAmount()); setShowPaymentModal(true); };

  // recordPayment() ka naam pehle se hi match karta hai, isme koi change nahi
  const handlePaymentSubmit = async () => {
    const result = await invoiceController.recordPayment(selected.id, { amount: paymentAmount });
    if (result.success) { setShowPaymentModal(false); fetchInvoices(); alert('✅ Payment recorded!'); }
    else alert('Error: ' + result.message);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filtered.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  const stats = {
    total: invoices.length,
    paid: invoices.filter((i) => i.status === 'Paid').length,
    overdue: invoices.filter((i) => i.isOverdue()).length,
    outstanding: invoices.reduce((sum, i) => sum + i.getBalanceAmount(), 0),
  };

  const styles = {
    container: { padding: 0 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' },
    title: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b', margin: 0 },
    button: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
    secondaryButton: { backgroundColor: '#fff', color: '#1e293b', border: '1px solid #e2e8f0' },
    statsBar: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' },
    statCard: { backgroundColor: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    statLabel: { fontSize: '14px', color: '#64748b', marginBottom: '8px' },
    statValue: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b' },
    filterBar: { display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' },
    searchBox: { flex: 1, minWidth: '250px', position: 'relative' },
    searchInput: { width: '100%', padding: '10px 10px 10px 40px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none' },
    searchIcon: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' },
    select: { padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', backgroundColor: '#fff', cursor: 'pointer' },
    card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { backgroundColor: '#f8fafc', padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#475569', borderBottom: '1px solid #e2e8f0' },
    td: { padding: '16px', borderBottom: '1px solid #e2e8f0', fontSize: '14px', color: '#1e293b' },
    badge: { display: 'inline-block', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
    actionButton: { padding: '6px', backgroundColor: 'transparent', border: 'none', borderRadius: '6px', cursor: 'pointer' },
    pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderTop: '1px solid #e2e8f0' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: '#fff', borderRadius: '12px', width: '90%', maxWidth: '700px', maxHeight: '90vh', overflow: 'auto' },
    modalHeader: { padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    modalTitle: { fontSize: '20px', fontWeight: '600', color: '#1e293b', margin: 0 },
    modalBody: { padding: '24px' },
    formGroup: { marginBottom: '20px' },
    label: { display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' },
    input: { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none' },
    errorList: { backgroundColor: '#fee2e2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px' },
    errorItem: { color: '#dc2626', fontSize: '14px', marginBottom: '4px' },
    modalFooter: { padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Invoices</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button style={{ ...styles.button, ...styles.secondaryButton }}><Download size={18} /> Export</button>
          <button style={styles.button} onClick={handleAdd}><Plus size={18} /> Add Invoice</button>
        </div>
      </div>

      <div style={styles.statsBar}>
        <div style={styles.statCard}><div style={styles.statLabel}>Total Invoices</div><div style={styles.statValue}>{stats.total}</div></div>
        <div style={styles.statCard}><div style={styles.statLabel}>Paid</div><div style={{ ...styles.statValue, color: '#10b981' }}>{stats.paid}</div></div>
        <div style={styles.statCard}><div style={styles.statLabel}>Overdue</div><div style={{ ...styles.statValue, color: '#ef4444' }}>{stats.overdue}</div></div>
        <div style={styles.statCard}><div style={styles.statLabel}>Outstanding</div><div style={{ ...styles.statValue, color: '#f59e0b' }}>₹{stats.outstanding.toLocaleString()}</div></div>
      </div>

      <div style={styles.filterBar}>
        <div style={styles.searchBox}>
          <Search size={18} color="#94a3b8" style={styles.searchIcon} />
          <input type="text" placeholder="Search invoices..." style={styles.searchInput} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>
        <select style={styles.select} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="All">All Status</option>
          <option value="Draft">Draft</option>
          <option value="Sent">Sent</option>
          <option value="Partial">Partial</option>
          <option value="Paid">Paid</option>
          <option value="Overdue">Overdue</option>
          <option value="Cancelled">Cancelled</option>
        </select>
      </div>

      <div style={styles.card}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading...</div>
        ) : currentItems.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No invoices found.</div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>ID</th>
                  <th style={styles.th}>Customer</th>
                  <th style={styles.th}>Due Date</th>
                  <th style={styles.th}>Total</th>
                  <th style={styles.th}>Balance</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((i) => (
                  <tr key={i.id}>
                    <td style={styles.td}><div style={{ fontWeight: '500', fontSize: '12px', color: '#64748b' }}>{i.invoiceId}</div></td>
                    <td style={styles.td} onClick={() => handleView(i)}><div style={{ fontWeight: '500' }}>{i.customerName}</div><div style={{ fontSize: '12px', color: '#64748b' }}>{i.company || 'N/A'}</div></td>
                    <td style={styles.td}>{i.dueDate ? new Date(i.dueDate).toLocaleDateString() : '-'}</td>
                    <td style={styles.td}>₹{Number(i.totalAmount).toLocaleString()}</td>
                    <td style={styles.td}>₹{i.getBalanceAmount().toLocaleString()}</td>
                    <td style={styles.td}><span style={{ ...styles.badge, backgroundColor: `${i.getStatusColor()}20`, color: i.getStatusColor() }}>{i.isOverdue() ? 'Overdue' : i.status}</span></td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button style={styles.actionButton} onClick={() => handleView(i)} title="View"><Eye size={18} color="#64748b" /></button>
                        <button style={styles.actionButton} onClick={() => handleEdit(i)} title="Edit"><Edit2 size={18} color="#3b82f6" /></button>
                        {i.getBalanceAmount() > 0 && <button style={styles.actionButton} onClick={() => handleRecordPayment(i)} title="Record Payment"><Wallet size={18} color="#10b981" /></button>}
                        <button style={styles.actionButton} onClick={() => handleDelete(i)} title="Delete"><Trash2 size={18} color="#ef4444" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={styles.pagination}>
              <div style={{ fontSize: '14px', color: '#64748b' }}>Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filtered.length)} of {filtered.length}</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
                <div style={{ padding: '0 12px', display: 'flex', alignItems: 'center' }}>Page {currentPage} of {totalPages}</div>
                <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>

      {(showAddModal || showEditModal) && (
        <div style={styles.modal} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{showAddModal ? 'Add Invoice' : 'Edit Invoice'}</h2>
              <button style={styles.actionButton} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}><X size={20} /></button>
            </div>
            <form onSubmit={showAddModal ? handleAddSubmit : handleEditSubmit}>
              <div style={styles.modalBody}>
                {formErrors.length > 0 && (<div style={styles.errorList}>{formErrors.map((err, i) => <div key={i} style={styles.errorItem}><AlertCircle size={14} style={{ display: 'inline', marginRight: '8px' }} />{err}</div>)}</div>)}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={styles.formGroup}><label style={styles.label}>Customer Name *</label><input type="text" name="customerName" style={styles.input} value={formData.customerName} onChange={handleInputChange} required /></div>
                  <div style={styles.formGroup}><label style={styles.label}>Email *</label><input type="email" name="email" style={styles.input} value={formData.email} onChange={handleInputChange} required /></div>
                  <div style={styles.formGroup}><label style={styles.label}>Phone *</label><input type="tel" name="phone" style={styles.input} value={formData.phone} onChange={handleInputChange} required /></div>
                  <div style={styles.formGroup}><label style={styles.label}>Company</label><input type="text" name="company" style={styles.input} value={formData.company} onChange={handleInputChange} /></div>
                  <div style={styles.formGroup}><label style={styles.label}>Due Date</label><input type="date" name="dueDate" style={styles.input} value={formData.dueDate} onChange={handleInputChange} /></div>
                  <div style={styles.formGroup}><label style={styles.label}>Status</label><select name="status" style={styles.input} value={formData.status} onChange={handleInputChange}><option value="Draft">Draft</option><option value="Sent">Sent</option><option value="Partial">Partial</option><option value="Paid">Paid</option><option value="Overdue">Overdue</option><option value="Cancelled">Cancelled</option></select></div>
                  <div style={styles.formGroup}><label style={styles.label}>Total Amount (₹)</label><input type="number" name="totalAmount" style={styles.input} value={formData.totalAmount} onChange={handleInputChange} min="0" /></div>
                  <div style={styles.formGroup}><label style={styles.label}>Paid Amount (₹)</label><input type="number" name="paidAmount" style={styles.input} value={formData.paidAmount} onChange={handleInputChange} min="0" /></div>
                </div>
                <div style={styles.formGroup}><label style={styles.label}>Notes</label><input type="text" name="notes" style={styles.input} value={formData.notes} onChange={handleInputChange} /></div>
              </div>
              <div style={styles.modalFooter}>
                <button type="button" style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => { setShowAddModal(false); setShowEditModal(false); }} disabled={formSubmitting}>Cancel</button>
                <button type="submit" style={styles.button} disabled={formSubmitting}>{formSubmitting ? 'Saving...' : (showAddModal ? 'Create' : 'Update')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showViewModal && selected && (
        <div style={styles.modal} onClick={() => setShowViewModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Invoice Details</h2>
              <button style={styles.actionButton} onClick={() => setShowViewModal(false)}><X size={20} /></button>
            </div>
            <div style={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <div><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Invoice ID</div><div style={{ fontSize: '16px', fontWeight: '500' }}>{selected.invoiceId}</div></div>
                <div><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Customer</div><div style={{ fontSize: '16px', fontWeight: '500' }}>{selected.customerName}</div></div>
                <div><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Status</div><span style={{ ...styles.badge, backgroundColor: `${selected.getStatusColor()}20`, color: selected.getStatusColor() }}>{selected.status}</span></div>
                <div><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Total Amount</div><div style={{ fontSize: '16px', fontWeight: '500' }}>₹{Number(selected.totalAmount).toLocaleString()}</div></div>
                <div><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Paid Amount</div><div style={{ fontSize: '16px', fontWeight: '500' }}>₹{Number(selected.paidAmount).toLocaleString()}</div></div>
                <div><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Balance</div><div style={{ fontSize: '16px', fontWeight: '500' }}>₹{selected.getBalanceAmount().toLocaleString()}</div></div>
              </div>
              {selected.notes && (<div style={{ marginTop: '24px' }}><div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>Notes</div><div style={{ padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', fontSize: '14px' }}>{selected.notes}</div></div>)}
            </div>
            <div style={styles.modalFooter}>
              {selected.getBalanceAmount() > 0 && (<button style={{ ...styles.button, backgroundColor: '#10b981' }} onClick={() => { setShowViewModal(false); handleRecordPayment(selected); }}><Wallet size={16} /> Record Payment</button>)}
              <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => { setShowViewModal(false); handleEdit(selected); }}><Edit2 size={16} /> Edit</button>
              <button style={styles.button} onClick={() => setShowViewModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showPaymentModal && selected && (
        <div style={styles.modal} onClick={() => setShowPaymentModal(false)}>
          <div style={{ ...styles.modalContent, maxWidth: '400px' }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Record Payment</h2>
              <button style={styles.actionButton} onClick={() => setShowPaymentModal(false)}><X size={20} /></button>
            </div>
            <div style={styles.modalBody}>
              <div style={{ backgroundColor: '#f8fafc', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
                <div style={{ fontSize: '14px', marginBottom: '8px' }}><strong>Customer:</strong> {selected.customerName}</div>
                <div style={{ fontSize: '14px' }}><strong>Balance Due:</strong> ₹{selected.getBalanceAmount().toLocaleString()}</div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Payment Amount (₹)</label>
                <input type="number" style={styles.input} value={paymentAmount} onChange={(e) => setPaymentAmount(Number(e.target.value))} min="0" max={selected.getBalanceAmount()} />
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => setShowPaymentModal(false)}>Cancel</button>
              <button style={{ ...styles.button, backgroundColor: '#10b981' }} onClick={handlePaymentSubmit}><CheckCircle size={16} /> Record</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesPage;